// ==========================
// Version 14 — components/UserMap.js (mapId wiring)
// - Uses withBrandMapOptions() so Map ID is applied when present
// - Keeps v13 InfoWindow colors and logic
// ==========================
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { fetchUserMemories } from '../db';
import { MAPS_LOADER_OPTIONS, withBrandMapOptions } from '../mapsConfig';

const containerStyle = { width: '100%', height: '420px', borderRadius: '12px' };
const fallbackCenter = { lat: 51.5074, lng: -0.1278 }; // London

const NAVY = '#0A1A33';
const GOLD = '#C9A44C';
const BURGUNDY = '#5C1A2B';
const TITLE_COLOR = NAVY;
const TEXT_COLOR = '#1b1b1b';
const META_COLOR = '#4a4a4a';

function pinUrl({
  body = NAVY,
  ring = GOLD,
  highlight = '#ffffff',
  shadow = 'rgba(0,0,0,0.25)',
} = {}) {
  const svg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="44" height="56" viewBox="0 0 44 56">
    <ellipse cx="22" cy="52" rx="10" ry="4" fill="${shadow}"/>
    <path d="M22 2c10 0 18 7.8 18 17.4C40 33.4 22 54 22 54S4 33.4 4 19.4C4 9.8 12 2 22 2z" fill="${body}"/>
    <path d="M16 7c-5 3.5-8 9-8 12.9C8 31 22 47 22 47V5.5C19.5 5.8 17.6 6.4 16 7z" fill="${highlight}" opacity="0.10"/>
    <circle cx="22" cy="19" r="7.5" fill="none" stroke="${ring}" stroke-width="2.5"/>
    <circle cx="22" cy="19" r="4.2" fill="${ring}"/>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export default function UserMap({ userId }) {
  const { isLoaded, loadError } = useJsApiLoader(MAPS_LOADER_OPTIONS);

  const [pins, setPins] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const mapRef = useRef(null);
  const didFitRef = useRef(false);

  useEffect(() => {
    if (!userId) { setPins([]); return; }
    (async () => {
      const docs = await fetchUserMemories(userId);
      const items = (docs || []).map(d => {
        const lat = d?.location?.lat ?? d?.lat;
        const lng = d?.location?.lng ?? d?.lng;
        return {
          id: d.id,
          lat: Number(lat),
          lng: Number(lng),
          date: d.date ?? null,
          city: d.city ?? null,
          image: Array.isArray(d.imageUrls) && d.imageUrls.length ? d.imageUrls[0] : null,
        };
      });
      setPins(items);
      didFitRef.current = false;
    })();
  }, [userId]);

  const safePins = useMemo(
    () => pins.filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng)),
    [pins]
  );

  const fitAll = useCallback((map, allPins) => {
    if (!map || !window.google || !allPins.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    allPins.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
    map.fitBounds(bounds);
  }, []);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (safePins.length && !didFitRef.current) {
      fitAll(map, safePins);
      didFitRef.current = true;
    } else {
      map.setCenter(fallbackCenter);
    }
  }, [fitAll, safePins]);

  useEffect(() => {
    if (mapRef.current && safePins.length && !didFitRef.current) {
      fitAll(mapRef.current, safePins);
      didFitRef.current = true;
    }
  }, [safePins, fitAll]);

  if (loadError) return <div style={{ color: BURGUNDY }}>Failed to load Google Maps.</div>;
  if (!isLoaded) return <div>Loading map…</div>;

  const activePin = activeId ? safePins.find(p => p.id === activeId) : null;

  const icon = {
    url: pinUrl({}),
    scaledSize: { width: 44, height: 56 },
    anchor: { x: 22, y: 54 },
  };

  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ margin: '0 0 8px' }}>Your Memories on the Map</h3>

      <GoogleMap
        mapContainerStyle={containerStyle}
        onLoad={onMapLoad}
        options={withBrandMapOptions()}
      >
        {safePins.map(p => (
          <Marker
            key={p.id}
            position={{ lat: p.lat, lng: p.lng }}
            onClick={() => setActiveId(p.id)}
            icon={icon}
          />
        ))}

        {activePin && (
          <InfoWindow
            position={{ lat: activePin.lat, lng: activePin.lng }}
            onCloseClick={() => setActiveId(null)}
          >
            <div style={{ maxWidth: 220, color: TEXT_COLOR, fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Inter, sans-serif' }}>
              {activePin.image && (
                <img
                  src={activePin.image}
                  alt=""
                  style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 6, marginBottom: 8 }}
                />
              )}
              <div style={{ fontWeight: 700, marginBottom: 4, color: TITLE_COLOR }}>
                {activePin.city || 'Your memory'}
              </div>
              <div style={{ fontSize: 13, color: META_COLOR }}>
                📅 {activePin.date ? String(activePin.date) : 'No date'}
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
}
// ==========================
// End Version 14 — components/UserMap.js
// ==========================
